import {
  Accordion,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import {
  IconExternalLink,
  IconFileText,
  IconLink,
  IconPencilPlus,
  IconRefresh,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDatasetsFilesList, type DatasetsFilesInfo } from '@mygpt/app';
import {
  answerKnowledgeGap,
  listKnowledgeGaps,
  listKnowledgeUnits,
  patchKnowledgeGap,
  recheckKnowledgeGap,
  type KnowledgeGapGroup,
  type KnowledgeGapRecheckResult,
  type KnowledgeUnitSummary,
} from '@mygpt/app/apis/services/knowledgeLoop';
import { UnitDrawer } from './UnitDrawer';
import { CARD_BORDER, errorDetail, formatJst, unitStatusColor } from './shared';

type View = 'open' | 'linked' | 'excluded';

interface Props {
  datasetId: string;
  onChanged?: () => void;
}

/** 需要（ナレッジ不足）キュー：AI が答えられなかった質問を束ごとに見て、書く・紐付ける・対象外にする（PM 評審 K04） */
export const GapQueue = ({ datasetId, onChanged }: Props) => {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('open');
  const [groups, setGroups] = useState<KnowledgeGapGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [answering, setAnswering] = useState<KnowledgeGapGroup | null>(null);
  const [linking, setLinking] = useState<KnowledgeGapGroup | null>(null);
  const [assigning, setAssigning] = useState<KnowledgeGapGroup | null>(null);
  const [openUnit, setOpenUnit] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', body: '', similar: [] as string[] });
  const [assignee, setAssignee] = useState('');
  const [unitQuery, setUnitQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(unitQuery, 300);
  const [unitOptions, setUnitOptions] = useState<KnowledgeUnitSummary[]>([]);
  const [unitChoice, setUnitChoice] = useState<string | null>(null);
  // 紐付け先：台帳の単元 か ナレッジベースのドキュメント
  const [linkMode, setLinkMode] = useState<'unit' | 'file'>('unit');
  const [fileOptions, setFileOptions] = useState<DatasetsFilesInfo[]>([]);
  const [fileChoice, setFileChoice] = useState<string | null>(null);
  // 再確認で「まだ答えられない」だった束：理由と見つかった根拠を行内に出す
  const [recheckResults, setRecheckResults] = useState<Record<string, KnowledgeGapRecheckResult>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listKnowledgeGaps(datasetId, { status: view, limit: 100 });
      setGroups(data.items || []);
    } catch (e) {
      console.error('load gaps failed:', e);
      showNotification({ color: 'red', message: t('knowledge_loop.load_error') });
    } finally {
      setLoading(false);
    }
  }, [datasetId, view, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!linking) return;
    listKnowledgeUnits(datasetId, { status: 'approved,candidate,scheduled', q: debouncedQuery || undefined, limit: 20 })
      .then(({ data }) => setUnitOptions(data.items || []))
      .catch(() => setUnitOptions([]));
  }, [datasetId, linking, debouncedQuery]);

  useEffect(() => {
    if (!linking || linkMode !== 'file') return;
    getDatasetsFilesList({
      id: datasetId,
      file_type: 'document',
      page: 1,
      size: 20,
      query: debouncedQuery || undefined,
    })
      .then(({ data }) => setFileOptions(data.items || []))
      .catch(() => setFileOptions([]));
  }, [datasetId, linking, linkMode, debouncedQuery]);

  const done = (message: string) => {
    showNotification({ color: 'green', message });
    load();
    onChanged?.();
  };

  const patch = async (g: KnowledgeGapGroup, body: Parameters<typeof patchKnowledgeGap>[2], message: string) => {
    setBusy(g.cluster_key);
    try {
      await patchKnowledgeGap(datasetId, g.items[0].id, { ...body, apply_to_cluster: true });
      done(message);
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  const recheck = async (g: KnowledgeGapGroup) => {
    setBusy(`recheck:${g.cluster_key}`);
    try {
      const { data } = await recheckKnowledgeGap(datasetId, g.items[0].id);
      if (data.answerable) {
        setRecheckResults((r) => {
          const next = { ...r };
          delete next[g.cluster_key];
          return next;
        });
        done(t('knowledge_loop.gaps.recheck_ok', { name: data.hits[0]?.name || '' }));
      } else {
        setRecheckResults((r) => ({ ...r, [g.cluster_key]: data }));
        showNotification({
          color: 'yellow',
          message: t('knowledge_loop.gaps.recheck_ng', {
            reason: t(`knowledge_loop.gaps.recheck_reason.${data.reason}`, data.reason),
          }),
        });
      }
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.gaps.recheck_error') });
    } finally {
      setBusy(null);
    }
  };

  const submitAnswer = async () => {
    if (!answering) return;
    setBusy('answer');
    try {
      const { data } = await answerKnowledgeGap(datasetId, answering.items[0].id, {
        title: form.title.trim(),
        body: form.body.trim(),
        similar_questions: form.similar,
        apply_to_cluster: true,
      });
      setAnswering(null);
      done(t('knowledge_loop.gaps.answered', { count: data.linked }));
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  const openAnswer = (g: KnowledgeGapGroup) => {
    setForm({
      title: g.sample_question.slice(0, 200),
      body: '',
      similar: g.items.slice(1, 4).map((i) => i.question.slice(0, 200)),
    });
    setAnswering(g);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v as View)}
          data={(['open', 'linked', 'excluded'] as View[]).map((v) => ({
            value: v,
            label: t(`knowledge_loop.gaps.view_${v}`),
          }))}
        />
        <Text size="xs" c="gray.6">
          {t('knowledge_loop.gaps.total', { count: groups.length })}
        </Text>
      </Group>
      <Text size="xs" c="gray.6">
        {t(`knowledge_loop.gaps.view_${view}_desc`)}
      </Text>

      {loading ? (
        <Group justify="center" py={60}>
          <Loader size="sm" />
        </Group>
      ) : groups.length === 0 ? (
        <Stack align="center" gap={4} py={60}>
          <Text fw={500}>{t('knowledge_loop.gaps.empty')}</Text>
          <Text size="xs" c="gray.6" ta="center" maw={460}>
            {t('knowledge_loop.gaps.empty_desc')}
          </Text>
        </Stack>
      ) : (
        <Accordion variant="separated" radius="md" chevronPosition="left" multiple>
          {groups.map((g) => (
            <Accordion.Item
              key={g.cluster_key}
              value={g.cluster_key}
              style={{ border: CARD_BORDER, background: '#fff' }}
            >
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" gap="md">
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} lineClamp={1}>
                      {g.sample_question}
                    </Text>
                    <Group gap={8}>
                      <Badge size="xs" variant="light" color={g.count > 2 ? 'orange' : 'gray'}>
                        {t('knowledge_loop.gaps.occurrences', { count: g.count })}
                      </Badge>
                      <Text size="xs" c="gray.6">
                        {t('knowledge_loop.gaps.last_seen', { date: formatJst(g.last_seen_at) })}
                      </Text>
                      {g.assignee && (
                        <Badge size="xs" variant="outline" color="gray" leftSection={<IconUser size={10} />}>
                          {g.assignee}
                        </Badge>
                      )}
                      {g.linked_unit && (
                        <Badge
                          size="xs"
                          variant="light"
                          color={unitStatusColor(g.linked_unit.status)}
                          style={{ cursor: 'pointer', textTransform: 'none', maxWidth: 320 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenUnit(g.linked_unit!.id);
                          }}
                        >
                          {t('knowledge_loop.gaps.linked_to', {
                            title: g.linked_unit.title,
                            status: t(`knowledge_loop.unit_status.${g.linked_unit.status}`),
                          })}
                        </Badge>
                      )}
                      {g.linked_ref && (
                        <Badge
                          size="xs"
                          variant="light"
                          color={g.items[0]?.resolution === 'covered' ? 'teal' : 'blue'}
                          leftSection={<IconFileText size={10} />}
                          style={{ textTransform: 'none', maxWidth: 360 }}
                        >
                          {t(
                            g.items[0]?.resolution === 'covered'
                              ? 'knowledge_loop.gaps.covered_by'
                              : 'knowledge_loop.gaps.linked_file',
                            { name: g.linked_ref.name }
                          )}
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Stack gap={4}>
                    {g.items.map((it) => (
                      <Group key={it.id} gap={8} wrap="nowrap" align="flex-start">
                        <Badge size="xs" variant="outline" color="gray" style={{ flexShrink: 0 }}>
                          {it.source_ref}
                        </Badge>
                        <Text size="xs" style={{ flex: 1 }}>
                          {it.question}
                        </Text>
                        {it.skip_detail && (
                          <Text size="xs" c="gray.5" style={{ flexShrink: 0 }}>
                            {t(`knowledge_loop.gaps.skip_detail.${it.skip_detail}`, it.skip_detail)}
                          </Text>
                        )}
                        {it.url && (
                          <Anchor href={it.url} target="_blank" rel="noreferrer" size="xs" style={{ flexShrink: 0 }}>
                            <IconExternalLink size={12} />
                          </Anchor>
                        )}
                      </Group>
                    ))}
                  </Stack>
                  {recheckResults[g.cluster_key] && !recheckResults[g.cluster_key].answerable && (
                    <Text size="xs" c="orange.7">
                      {t('knowledge_loop.gaps.recheck_ng', {
                        reason: t(
                          `knowledge_loop.gaps.recheck_reason.${recheckResults[g.cluster_key].reason}`,
                          recheckResults[g.cluster_key].reason
                        ),
                      })}
                      {recheckResults[g.cluster_key].hits.length > 0 &&
                        ` ・ ${t('knowledge_loop.gaps.recheck_hits', {
                          names: recheckResults[g.cluster_key].hits.map((h) => h.name).join(' / '),
                        })}`}
                    </Text>
                  )}
                  <Group gap={8}>
                    {view === 'open' && (
                      <Button
                        size="xs"
                        variant="light"
                        color="teal"
                        leftSection={<IconRefresh size={14} />}
                        loading={busy === `recheck:${g.cluster_key}`}
                        onClick={() => recheck(g)}
                      >
                        {t('knowledge_loop.gaps.recheck')}
                      </Button>
                    )}
                    {view !== 'excluded' && !g.linked_unit && !g.linked_ref && (
                      <Button size="xs" leftSection={<IconPencilPlus size={14} />} onClick={() => openAnswer(g)}>
                        {t('knowledge_loop.gaps.write_answer')}
                      </Button>
                    )}
                    {view !== 'excluded' && (
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconLink size={14} />}
                        onClick={() => {
                          setUnitQuery('');
                          setUnitChoice(null);
                          setFileChoice(null);
                          setLinkMode('unit');
                          setLinking(g);
                        }}
                      >
                        {t('knowledge_loop.gaps.link_existing')}
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="default"
                      leftSection={<IconUser size={14} />}
                      onClick={() => {
                        setAssignee(g.assignee || '');
                        setAssigning(g);
                      }}
                    >
                      {t('knowledge_loop.gaps.assign')}
                    </Button>
                    {view === 'open' ? (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        leftSection={<IconX size={14} />}
                        loading={busy === g.cluster_key}
                        onClick={() => patch(g, { resolution: 'excluded' }, t('knowledge_loop.gaps.excluded'))}
                      >
                        {t('knowledge_loop.gaps.exclude')}
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        loading={busy === g.cluster_key}
                        onClick={() => patch(g, { resolution: 'gap_open' }, t('knowledge_loop.gaps.reopened'))}
                      >
                        {t('knowledge_loop.gaps.reopen')}
                      </Button>
                    )}
                  </Group>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      <Modal
        opened={!!answering}
        onClose={() => setAnswering(null)}
        centered
        size="lg"
        title={t('knowledge_loop.gaps.write_answer')}
      >
        <Stack gap="sm">
          <Text size="xs" c="gray.6">
            {t('knowledge_loop.gaps.write_answer_desc', { count: answering?.count || 0 })}
          </Text>
          <TextInput
            label={t('knowledge_loop.review.field_title')}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.currentTarget.value }))}
          />
          <Textarea
            label={t('knowledge_loop.review.field_body')}
            autosize
            minRows={5}
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.currentTarget.value }))}
          />
          <TagsInput
            label={t('knowledge_loop.review.field_similar')}
            value={form.similar}
            onChange={(v) => setForm((f) => ({ ...f, similar: v.slice(0, 5) }))}
          />
          <Group justify="flex-end" gap={8}>
            <Button size="xs" variant="default" onClick={() => setAnswering(null)}>
              {t('knowledge_loop.cancel')}
            </Button>
            <Button
              size="xs"
              loading={busy === 'answer'}
              disabled={form.title.trim().length < 5 || form.body.trim().length < 10}
              onClick={submitAnswer}
            >
              {t('knowledge_loop.gaps.submit_answer')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!linking}
        onClose={() => setLinking(null)}
        centered
        title={t('knowledge_loop.gaps.link_existing')}
      >
        <Stack gap="sm">
          <SegmentedControl
            size="xs"
            value={linkMode}
            onChange={(v) => {
              setLinkMode(v as 'unit' | 'file');
              setUnitChoice(null);
              setFileChoice(null);
            }}
            data={[
              { value: 'unit', label: t('knowledge_loop.gaps.link_mode_unit') },
              { value: 'file', label: t('knowledge_loop.gaps.link_mode_file') },
            ]}
          />
          <TextInput
            placeholder={t('knowledge_loop.ledger.search')}
            value={unitQuery}
            onChange={(e) => setUnitQuery(e.currentTarget.value)}
          />
          {linkMode === 'unit' ? (
            <Select
              data={unitOptions.map((u) => ({
                value: u.id,
                label: `${u.title}（${t(`knowledge_loop.unit_status.${u.status}`)}）`,
              }))}
              value={unitChoice}
              onChange={setUnitChoice}
              placeholder={t('knowledge_loop.gaps.pick_unit')}
              searchable
              nothingFoundMessage={t('knowledge_loop.ledger.empty')}
            />
          ) : (
            <Select
              data={fileOptions.map((f) => ({ value: f.id, label: f.title || f.filename }))}
              value={fileChoice}
              onChange={setFileChoice}
              placeholder={t('knowledge_loop.gaps.pick_file')}
              searchable
              nothingFoundMessage={t('no_data')}
            />
          )}
          <Group justify="flex-end" gap={8}>
            <Button size="xs" variant="default" onClick={() => setLinking(null)}>
              {t('knowledge_loop.cancel')}
            </Button>
            <Button
              size="xs"
              disabled={linkMode === 'unit' ? !unitChoice : !fileChoice}
              loading={busy === linking?.cluster_key}
              onClick={async () => {
                if (!linking) return;
                const ref = linkMode === 'unit' ? unitChoice : fileChoice ? `file:${fileChoice}` : null;
                if (!ref) return;
                await patch(linking, { resolution_ref: ref }, t('knowledge_loop.gaps.linked'));
                setLinking(null);
              }}
            >
              {t('knowledge_loop.gaps.link_existing')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!assigning}
        onClose={() => setAssigning(null)}
        centered
        size="sm"
        title={t('knowledge_loop.gaps.assign')}
      >
        <Stack gap="sm">
          <TextInput
            value={assignee}
            onChange={(e) => setAssignee(e.currentTarget.value)}
            placeholder="name@example.com"
          />
          <Group justify="flex-end" gap={8}>
            <Button size="xs" variant="default" onClick={() => setAssigning(null)}>
              {t('knowledge_loop.cancel')}
            </Button>
            <Button
              size="xs"
              loading={busy === assigning?.cluster_key}
              onClick={async () => {
                if (!assigning) return;
                await patch(assigning, { assignee: assignee.trim() }, t('knowledge_loop.gaps.assigned'));
                setAssigning(null);
              }}
            >
              {t('knowledge_loop.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <UnitDrawer datasetId={datasetId} unitId={openUnit} onClose={() => setOpenUnit(null)} onReviewed={load} />
    </Stack>
  );
};
